import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface ReportingDates {
  censusStartDate: string;
  healthStartDate: string;
  revenueStartDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReportDatesService {
  private apiUrl = environment.environmentDetails + 'community-service/rest/communityservice/';
  private reportingDatesSubject = new BehaviorSubject<ReportingDates | null>(null);
  public reportingDates$ = this.reportingDatesSubject.asObservable();

  constructor(private http: HttpClient) {
    // Initialize with stored dates if available
    const storedDates = this.getStoredReportingDates();
    if (storedDates) {
      this.reportingDatesSubject.next(storedDates);
    }
  }

  /**
   * Get community reporting dates from API or localStorage
   */
  getCommunityReportingDates(communityId: number, forceRefresh: boolean = false): Observable<ReportingDates | null> {
    // Check localStorage first unless force refresh is requested
    if (!forceRefresh) {
      const dates = this.getStoredReportingDates();
      if (dates) {
        return of(dates);
      }
    }

    // Fetch from API
    return this.http.post<any>(`${this.apiUrl}getCommunityReportingDates`, { communityId }).pipe(
      map(response => {
        if (response && response.status === 1 && response.data) {
          const dates: ReportingDates = {
            censusStartDate: response.data.censusStartDate,
            healthStartDate: response.data.healthStartDate,
            revenueStartDate: response.data.revenueStartDate
          };
          this.storeReportingDates(dates);
          return dates;
        }
        return null;
      })
    );
  }

  /**
   * Store reporting dates in localStorage
   */
  private storeReportingDates(dates: ReportingDates): void {
    localStorage.setItem('censusStartDate', dates.censusStartDate);
    localStorage.setItem('healthStartDate', dates.healthStartDate);
    localStorage.setItem('revenueStartDate', dates.revenueStartDate);
    localStorage.setItem('reportingDatesTimestamp', new Date().toISOString());

    // Emit the new dates to all subscribers
    this.reportingDatesSubject.next(dates);
  }

  /**
   * Get stored reporting dates from localStorage
   */
  getStoredReportingDates(): ReportingDates | null {
    const censusStartDate = localStorage.getItem('censusStartDate');
    const healthStartDate = localStorage.getItem('healthStartDate');
    const revenueStartDate = localStorage.getItem('revenueStartDate');

    if (censusStartDate && healthStartDate && revenueStartDate) {
      return {
        censusStartDate,
        healthStartDate,
        revenueStartDate
      };
    }
    return null;
  }

  /**
   * Get a specific reporting date
   */
  getReportingDate(type: 'census' | 'health' | 'revenue'): Date | null {
    const dateStr = localStorage.getItem(`${type}StartDate`);
    return dateStr ? new Date(dateStr) : null;
  }

  /**
   * Check if reporting dates exist in localStorage
   */
  hasReportingDates(): boolean {
    return !!(
      localStorage.getItem('censusStartDate') &&
      localStorage.getItem('healthStartDate') &&
      localStorage.getItem('revenueStartDate')
    );
  }

  /**
   * Clear reporting dates from localStorage
   */
  clearReportingDates(): void {
    localStorage.removeItem('censusStartDate');
    localStorage.removeItem('healthStartDate');
    localStorage.removeItem('revenueStartDate');
    localStorage.removeItem('reportingDatesTimestamp');

    // Clear the subject
    this.reportingDatesSubject.next(null);
  }

  /**
   * Update reporting dates (used when dates are saved)
   */
  updateReportingDates(dates: ReportingDates): void {
    this.storeReportingDates(dates);
  }

  /**
   * Get valid year range for a report type
   */
  getValidYearRange(reportType: 'census' | 'health' | 'revenue'): number[] {
    const startDate = this.getReportingDate(reportType);
    if (!startDate) {
      // If no start date, return last 5 years as default
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 5 }, (_, i) => currentYear - i);
    }

    const startYear = startDate.getFullYear();
    const currentYear = new Date().getFullYear();
    const years: number[] = [];

    for (let year = currentYear; year >= startYear; year--) {
      years.push(year);
    }

    return years;
  }

  /**
   * Get valid months for a specific year and report type
   */
  getValidMonths(year: number, reportType: 'census' | 'health' | 'revenue'): number[] {
    const startDate = this.getReportingDate(reportType);
    const currentDate = new Date();

    if (!startDate) {
      // If no start date, return all months
      return Array.from({ length: 12 }, (_, i) => i + 1);
    }

    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth() + 1; // JavaScript months are 0-indexed
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    if (year === startYear && year === currentYear) {
      // Both start and current year - return months between start and current
      return Array.from({ length: currentMonth - startMonth + 1 }, (_, i) => startMonth + i);
    } else if (year === startYear) {
      // Start year - return months from start month to December
      return Array.from({ length: 12 - startMonth + 1 }, (_, i) => startMonth + i);
    } else if (year === currentYear) {
      // Current year - return months from January to current month
      return Array.from({ length: currentMonth }, (_, i) => i + 1);
    } else if (year > startYear && year < currentYear) {
      // Years in between - return all months
      return Array.from({ length: 12 }, (_, i) => i + 1);
    }

    // Invalid year
    return [];
  }
}
